import React from "react";
import ForgeShell, { ControlLabel, Segmented, Chips, Textarea, OutputCard, CopyableText, EmptyState } from "./ForgeShell";
import { useForgeModule, GenerateButton, ActionRow, DraftsList } from "./useForgeModule";

const FORMATS = [
  { value: "landing", label: "Landing" },
  { value: "vsl", label: "VSL" },
  { value: "webinar", label: "Webinar" },
  { value: "long_form", label: "Long-form" },
  { value: "email_sales", label: "Email sales" },
];
const TONES = ["Premium", "Aggressive", "Simple", "Bold", "Story-led"];

export default function SalesCopy() {
  const m = useForgeModule("sales");
  const i = m.inputs;
  const set = (k, v) => m.setInputs({ ...i, [k]: v });
  const s = m.output?.sections || {};
  const variations = m.output?.variations || [];

  return (
    <ForgeShell
      testid="forge-sales-copy"
      left={
        <>
          <ControlLabel hint="Where this copy lives">Format</ControlLabel>
          <Segmented testid="sales-format" options={FORMATS} value={i.format || "landing"} onChange={(v) => set("format", v)} />

          <ControlLabel>Product / outcome</ControlLabel>
          <Textarea testid="sales-product" value={i.product} onChange={(v) => set("product", v)} rows={3}
            placeholder="6-week revenue OS for solo creators — predictable $20k months" />

          <ControlLabel>Audience pain</ControlLabel>
          <Textarea testid="sales-pain" value={i.pain} onChange={(v) => set("pain", v)} rows={3}
            placeholder="Stuck reactive — every dollar earned costs another 12h of replying to DMs" />

          <ControlLabel>Primary tone</ControlLabel>
          <Chips testid="sales-tone" options={TONES} value={i.tone || "Premium"} onChange={(v) => set("tone", v)} />

          <GenerateButton testid="sales-generate" loading={m.loading} onClick={m.generate} />
        </>
      }
      center={
        !m.output ? (
          <EmptyState title="Generate your conversion message" body="Headline · Problem · Transformation · Proof · CTA — plus 3 tone variations you can A/B." />
        ) : (
          <>
            <ActionRow testid="sales-actions" onRegenerate={m.generate} onSave={() => m.saveDraft(s.headline)} regenerating={m.loading} />

            <OutputCard title="Headline"><CopyableText testid="sales-headline" text={s.headline} /></OutputCard>
            <OutputCard title="Problem"><CopyableText testid="sales-problem" text={s.problem} /></OutputCard>
            <OutputCard title="Transformation"><CopyableText testid="sales-transformation" text={s.transformation} /></OutputCard>
            <OutputCard title="Proof"><CopyableText testid="sales-proof" text={s.proof} /></OutputCard>
            <OutputCard title="CTA"><CopyableText testid="sales-cta" text={s.cta} /></OutputCard>

            <OutputCard title="Tone variations">
              <ul className="space-y-3">
                {variations.map((v, idx) => (
                  <li key={idx} className="rounded-xl border border-line p-3 bg-zinc-50" data-testid={`sales-variation-${v.tone}`}>
                    <p className="text-[10px] uppercase tracking-wider text-meta font-bold mb-1.5">{v.tone}</p>
                    <p className="text-sm font-semibold text-ink">{v.headline}</p>
                    <p className="text-xs text-ai mt-1.5 font-medium">→ {v.cta}</p>
                  </li>
                ))}
              </ul>
            </OutputCard>
          </>
        )
      }
      right={
        <>
          <div>
            <ControlLabel hint="What this copy is going to do">Performance lens</ControlLabel>
            <ul className="mt-2 space-y-1.5 text-xs text-ink-soft">
              <li>· One headline. One promise.</li>
              <li>· Pain → transformation → proof.</li>
              <li>· CTA should sound inevitable.</li>
            </ul>
          </div>
          <DraftsList drafts={m.drafts} onLoad={m.loadDraft} onDelete={m.deleteDraft}
            onToggleFavorite={m.toggleFavorite} onArchive={m.archiveDraft}
            onDuplicate={m.duplicateDraft} onPromote={m.promoteDraft} module="sales" />
        </>
      }
    />
  );
}
